import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import axios from 'axios'
import { toast } from 'react-toastify'

const API_URL = process.env.REACT_APP_API_URL

const PO_STATUS_OPTIONS = [
  'Draft',
  'Internal issue',
  'Acknowledged',
  'SPO Created',
  'Dispatch in progress',
  'Partially Dispatched', 
  'Dispatched',
  'In Transit',
  'Delivered',
  'Replacement in progress',
  'GRN Completed',
  'Payment completed',
  'Order on Hold',
  'Order Closed'
]
const PAYMENT_STATUS_OPTIONS = ['Pending', 'Paid']
const STATUS_OPTIONS = ['Pending', 'Completed']
const PAYMENT_OPTIONS = ['Credit', 'Advance']

const SUPPLIER_SEARCH_MIN_LENGTH = 3
const SUPPLIER_SEARCH_DEBOUNCE_MS = 400

const TH_STYLE = {
  padding: '10px 6px',
  fontSize: '12px',
  fontWeight: '600',
  whiteSpace: 'nowrap'
}

const TD_STYLE = { padding: '6px', verticalAlign: 'middle' }

function createEmptyRow(id) {
  return {
    id,
    materialCode: '',
    itemDesc: '',
    uom: '',
    quantityOrdered: '',
    spoNo: '',
    spoDate: '',
    supplierName: '',
    supplierId: '',
    cpoBasicPrice: '',
    spoBasicPrice: '',
    payment: '',
    poStatus: 'Draft',
    quantityDispatched: '0',
    dispatchDate: '',
    deliveryDate: '',
    poReceivedDate: '',  
    grnStatus: 'Pending',
    paymentStatus: 'Pending',
    projectStatus: 'Pending'
  }
}

function parseApiResponse(raw) {
  let responseData = raw
  if (typeof responseData === 'string') {
    try {
      responseData = JSON.parse(responseData)
    } catch {
      return raw
    }
  }
  if (responseData?.body != null) {
    if (typeof responseData.body === 'string') {
      try {
        return JSON.parse(responseData.body)
      } catch {
        return responseData.body
      }
    }
    return responseData.body
  }
  return responseData
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const success = responseData.success
  return (
    success === true || success === 'true' || success === 1 || success === '1'
  )
}

function formatDateForApi(isoDate) {
  if (!isoDate) return ''
  const [yyyy, mm, dd] = isoDate.split('-')
  if (!yyyy || !mm || !dd) return ''
  return `${dd}-${mm}-${yyyy}`
}

function sanitizeDecimalInput(value) {
  const cleaned = String(value ?? '')
    .replace(/,/g, '')
    .replace(/\s/g, '')
    .replace(/[₹$]/g, '')
  if (cleaned === '' || /^\d*\.?\d*$/.test(cleaned)) return cleaned
  return null
}

function parseDecimal(value) {
  const cleaned = sanitizeDecimalInput(value)
  if (cleaned == null || cleaned === '' || cleaned === '.') return NaN
  return parseFloat(cleaned)
}

function getRowTotal(row) {
  const cpo = parseDecimal(row.cpoBasicPrice)
  const spo = parseDecimal(row.spoBasicPrice)
  const qty = parseDecimal(row.quantityOrdered)
  if (!Number.isFinite(cpo) || !Number.isFinite(spo) || !Number.isFinite(qty)) {
    return ''
  }
  return (cpo - spo) * qty
}

function formatTotalDisplay(total) {
  if (total === '') return ''
  return Number(total).toFixed(Number.isInteger(total) ? 0 : 2)
}

function rowHasAnyData(row) {
  return (
    (row.materialCode && row.materialCode.trim() !== '') ||
    (row.itemDesc && row.itemDesc.trim() !== '') ||
    (row.uom && row.uom.trim() !== '') ||
    (row.quantityOrdered && row.quantityOrdered.toString().trim() !== '') ||
    (row.spoNo && row.spoNo.trim() !== '') ||
    row.spoDate ||
    (row.supplierName && row.supplierName.trim() !== '') ||
    (row.cpoBasicPrice && row.cpoBasicPrice.toString().trim() !== '') ||
    (row.spoBasicPrice && row.spoBasicPrice.toString().trim() !== '') ||
    (row.quantityDispatched &&
      row.quantityDispatched.toString().trim() !== '' &&
      row.quantityDispatched !== '0') ||
    row.dispatchDate ||
    row.deliveryDate
  )
}

function validateFilledRow(row, rowIndex) {
  if (!row.materialCode.trim()) {
    toast.error(`Row ${rowIndex}: Material code is required`, { autoClose: 2000 })
    return false
  }
  if (!row.itemDesc.trim()) {
    toast.error(`Row ${rowIndex}: Material description is required`, {
      autoClose: 2000
    })
    return false
  }
  if (!row.uom.trim()) {
    toast.error(`Row ${rowIndex}: UOM is required`, { autoClose: 2000 })
    return false
  }
  if (row.quantityOrdered === '' || isNaN(parseFloat(row.quantityOrdered))) {
    toast.error(`Row ${rowIndex}: Valid quantity ordered is required`, {
      autoClose: 2000
    })
    return false
  }

  const isSpoCreated = row.poStatus === 'SPO Created'
  if (isSpoCreated) {
    if (!row.spoNo.trim()) {
      toast.error(
        `Row ${rowIndex}: SPO number is required when PO Status is SPO Created`,
        { autoClose: 2500 }
      )
      return false
    }
    if (!row.spoDate) {
      toast.error(
        `Row ${rowIndex}: SPO date is required when PO Status is SPO Created`,
        { autoClose: 2500 }
      )
      return false
    }
    if (!row.supplierName.trim() || !row.supplierId) {
      toast.error(
        `Row ${rowIndex}: Supplier is required when PO Status is SPO Created`,
        { autoClose: 2500 }
      )
      return false
    }
    if (row.cpoBasicPrice === '' || isNaN(parseDecimal(row.cpoBasicPrice))) {
      toast.error(
        `Row ${rowIndex}: CPO basic price is required when PO Status is SPO Created`,
        { autoClose: 2500 }
      )
      return false
    }
    if (row.spoBasicPrice === '' || isNaN(parseDecimal(row.spoBasicPrice))) {
      toast.error(
        `Row ${rowIndex}: SPO basic price is required when PO Status is SPO Created`,
        { autoClose: 2500 }
      )
      return false
    }
    if (!row.payment) {
      toast.error(
        `Row ${rowIndex}: Payment is required when PO Status is SPO Created`,
        { autoClose: 2500 }
      )
      return false
    }
  } else {
    if (
      row.cpoBasicPrice !== '' &&
      isNaN(parseDecimal(row.cpoBasicPrice))
    ) {
      toast.error(`Row ${rowIndex}: Valid CPO basic price is required`, {
        autoClose: 2000
      })
      return false
    }
    if (
      row.spoBasicPrice !== '' &&
      isNaN(parseDecimal(row.spoBasicPrice))
    ) {
      toast.error(`Row ${rowIndex}: Valid SPO basic price is required`, {
        autoClose: 2000
      })
      return false
    }
  }

  if (
    row.quantityDispatched !== '' &&
    isNaN(parseFloat(row.quantityDispatched))
  ) {
    toast.error(`Row ${rowIndex}: Valid quantity dispatched is required`, {
      autoClose: 2000
    })
    return false
  }
  const DISPATCH_STATUSES = ['Partially Dispatched', 'Dispatched', 'In Transit']
  if (DISPATCH_STATUSES.includes(row.poStatus)) {
    const dispatchedQty = parseDecimal(row.quantityDispatched)
    const orderedQty = parseDecimal(row.quantityOrdered)

    if (!Number.isFinite(dispatchedQty) || dispatchedQty <= 0) {
      toast.error(
        `Row ${rowIndex}: Quantity dispatched is required when PO Status is ${row.poStatus}`,
        { autoClose: 2500 }
      )
      return false
    }
    if (!row.dispatchDate) {
      toast.error(
        `Row ${rowIndex}: Dispatch date is required when PO Status is ${row.poStatus}`,
        { autoClose: 2500 }
      )
      return false
    }
    if (
      row.poStatus === 'Partially Dispatched' &&
      Number.isFinite(orderedQty) &&
      dispatchedQty >= orderedQty
    ) {
      toast.error(
        `Row ${rowIndex}: For Partially Dispatched, quantity dispatched must be less than quantity ordered`,
        { autoClose: 2500 }
      )
      return false
    }
  }

  return true
}

function getSupplierRecordId(supplier) {
  return String(
    supplier?.id || supplier?._id || supplier?.supplierId || ''
  ).trim()
}

function getSupplierDisplayName(supplier) {
  return String(
    supplier?.clientname ||
      supplier?.clientName ||
      supplier?.supplierName ||
      supplier?.name ||
      supplier?.companyName ||
      ''
  ).trim()
}

function extractCustomersList(payload) {
  const data = payload?.data != null ? payload.data : payload
  const list =
    (Array.isArray(data?.customers) && data.customers) ||
    (Array.isArray(data?.data) && data.data) ||
    (Array.isArray(payload?.customers) && payload.customers) ||
    (Array.isArray(data) && data) ||
    []
  return list
    .map((item) => ({
      _id: String(item?._id || item?.id || '').trim(),
      customerName: String(
        item?.customerName || item?.customer || item?.name || ''
      ).trim()
    }))
    .filter((item) => item._id && item.customerName)
    .sort((a, b) => a.customerName.localeCompare(b.customerName))
}

const TrackerAdd = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [commonData, setCommonData] = useState({
    customerId: '',
    customerName: '',
    poNo: '',
    poDate: ''
  })
  const [customers, setCustomers] = useState([])
  const [isCustomersLoading, setIsCustomersLoading] = useState(true)
  const [unitOptions, setUnitOptions] = useState([])
  const [isLoadingUnits, setIsLoadingUnits] = useState(false)
  const [trackerRows, setTrackerRows] = useState([createEmptyRow(1)])

  const [activeSupplierRowId, setActiveSupplierRowId] = useState(null)
  const [supplierSearchResults, setSupplierSearchResults] = useState([])
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false)
  const [supplierDropdownPos, setSupplierDropdownPos] = useState(null)
  const supplierSearchDebounceRef = useRef(null)
  const supplierSearchRequestIdRef = useRef(0)

  const fetchCustomers = useCallback(async () => {
    setIsCustomersLoading(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-all-customers',
        search: ''
      })
      const responseData = parseApiResponse(response.data)
      if (isApiSuccess(responseData)) {
        setCustomers(extractCustomersList(responseData))
      } else {
        setCustomers([])
      }
    } catch (error) {
      console.error('Fetch customers error:', error)
      setCustomers([])
      toast.error('Failed to load customers', { autoClose: 2000 })
    } finally {
      setIsCustomersLoading(false)
    }
  }, [])

  const fetchUnits = useCallback(async () => {
    setIsLoadingUnits(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-all-units',
        sortBy: 'unit',
        sortOrder: 'asc'
      })
      const responseData = parseApiResponse(response.data)
      const list = Array.isArray(responseData?.data?.units)
        ? responseData.data.units
        : []
      const activeUnits = list
        .map((item) => ({
          id: item?._id || item?.id || '',
          unit: String(item?.unit || '').trim(),
          status: String(item?.status || '').trim()
        }))
        .filter((item) => item.unit)
        .filter(
          (item) => !item.status || item.status.toLowerCase() === 'active'
        )
      setUnitOptions(isApiSuccess(responseData) ? activeUnits : [])
    } catch (error) {
      console.error('Fetch units error:', error)
      setUnitOptions([])
    } finally {
      setIsLoadingUnits(false)
    }
  }, [])

  useEffect(() => {
    fetchCustomers()
    fetchUnits()
  }, [fetchCustomers, fetchUnits])

  const updateSupplierDropdownPos = useCallback((rowId) => {
    const el = document.querySelector(`[data-supplier-input="${rowId}"]`)
    if (!el) {
      setSupplierDropdownPos(null)
      return
    }
    const rect = el.getBoundingClientRect()
    setSupplierDropdownPos({
      top: rect.bottom + 2,
      left: rect.left,
      width: Math.max(rect.width, 240)
    })
  }, [])

  const handleCommonChange = (e) => {
    const { name, value } = e.target
    setCommonData((prev) => ({ ...prev, [name]: value }))
  }

  const handleCustomerChange = (e) => {
    const customerId = String(e.target.value || '').trim()
    const selected = customers.find((c) => c._id === customerId)
    setCommonData((prev) => ({
      ...prev,
      customerId,
      customerName: selected?.customerName || ''
    }))
  }

  const searchSuppliers = useCallback(async (searchTerm) => {
    const search = String(searchTerm ?? '').trim()

    if (!search || search.length < SUPPLIER_SEARCH_MIN_LENGTH) {
      setSupplierSearchResults([])
      return
    }

    const requestId = ++supplierSearchRequestIdRef.current
    setSupplierSearchLoading(true)

    try {
      const userRole = localStorage.getItem('role')
      const payload = {
        action: 'get-suppliers',
        status: 'active',
        search
      }
      if (userRole !== 'admin') {
        payload.status = 'active'
      }

      const response = await axios.post(API_URL, payload)
      if (requestId !== supplierSearchRequestIdRef.current) return

      const responseData = parseApiResponse(response.data)
      const list = responseData?.suppliers || responseData?.data?.suppliers || []

      if (isApiSuccess(responseData) && Array.isArray(list) && list.length > 0) {
        setSupplierSearchResults(list)
      } else {
        setSupplierSearchResults([])
      }
    } catch (error) {
      if (requestId !== supplierSearchRequestIdRef.current) return
      console.error('Supplier search error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(errData?.message || 'Failed to search suppliers', {
        autoClose: 2000
      })
      setSupplierSearchResults([])
    } finally {
      if (requestId === supplierSearchRequestIdRef.current) {
        setSupplierSearchLoading(false)
      }
    }
  }, [])

  const handleSupplierNameChange = (rowId, value) => {
    setTrackerRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? { ...row, supplierName: value, supplierId: '' }
          : row
      )
    )
    setActiveSupplierRowId(rowId)
    updateSupplierDropdownPos(rowId)
    supplierSearchRequestIdRef.current += 1

    if (supplierSearchDebounceRef.current) {
      clearTimeout(supplierSearchDebounceRef.current)
    }

    const trimmed = value.trim()
    if (trimmed.length < SUPPLIER_SEARCH_MIN_LENGTH) {
      setSupplierSearchLoading(false)
      setSupplierSearchResults([])
      return
    }

    supplierSearchDebounceRef.current = setTimeout(() => {
      searchSuppliers(trimmed)
    }, SUPPLIER_SEARCH_DEBOUNCE_MS)
  }

  const handleSelectSupplier = (rowId, supplier) => {
    const name = getSupplierDisplayName(supplier)
    const id = getSupplierRecordId(supplier)
    if (!name || !id) {
      toast.error('Selected supplier is missing name or id', { autoClose: 2000 })
      return
    }
    setTrackerRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? { ...row, supplierName: name, supplierId: id }
          : row
      )
    )
    setSupplierSearchResults([])
    setActiveSupplierRowId(null)
    setSupplierDropdownPos(null)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest('[data-supplier-cell]') &&
        !event.target.closest('[data-supplier-dropdown]')
      ) {
        setActiveSupplierRowId(null)
        setSupplierDropdownPos(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (activeSupplierRowId == null) return undefined

    const syncPosition = () => updateSupplierDropdownPos(activeSupplierRowId)
    syncPosition()

    window.addEventListener('resize', syncPosition)
    window.addEventListener('scroll', syncPosition, true)
    return () => {
      window.removeEventListener('resize', syncPosition)
      window.removeEventListener('scroll', syncPosition, true)
    }
  }, [activeSupplierRowId, updateSupplierDropdownPos])

  useEffect(() => {
    return () => {
      if (supplierSearchDebounceRef.current) {
        clearTimeout(supplierSearchDebounceRef.current)
      }
    }
  }, [])

  const handleAddRow = () => {
    const nextId =
      trackerRows.length > 0
        ? Math.max(...trackerRows.map((r) => r.id)) + 1
        : 1
    setTrackerRows([...trackerRows, createEmptyRow(nextId)])
  }

  const handleRemoveRow = (id) => {
    if (trackerRows.length === 1) {
      toast.error('At least one row is required', { autoClose: 2000 })
      return
    }
    const updatedRows = trackerRows
      .filter((row) => row.id !== id)
      .map((row, index) => ({ ...row, id: index + 1 }))
    setTrackerRows(updatedRows)
    if (activeSupplierRowId === id) {
      setActiveSupplierRowId(null)
      setSupplierSearchResults([])
      setSupplierDropdownPos(null)
    }
  }

  const handleUpdateRow = (id, field, value) => {
    setTrackerRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    )
  }

  const handleUpdateRowNumeric = (id, field, value) => {
    const next = sanitizeDecimalInput(value)
    if (next !== null) {
      handleUpdateRow(id, field, next)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!commonData.customerId || !commonData.customerName.trim()) {
      toast.error('Please select a customer', { autoClose: 2000 })
      return
    }
    if (!commonData.poNo.trim()) {
      toast.error('PO number is required', { autoClose: 2000 })
      return
    }
    if (!commonData.poDate) {
      toast.error('PO date is required', { autoClose: 2000 })
      return
    }

    const userId = localStorage.getItem('id')
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      setTimeout(() => navigate('/'), 2000)
      return
    }

    const filledRows = trackerRows.filter(rowHasAnyData)
    if (filledRows.length === 0) {
      toast.error('Please fill at least one line item before submitting', {
        autoClose: 2000
      })
      return
    }

    for (let i = 0; i < filledRows.length; i++) {
      const displayIndex =
        trackerRows.findIndex((r) => r.id === filledRows[i].id) + 1
      if (!validateFilledRow(filledRows[i], displayIndex)) {
        return
      }
    }

    const emptyRowsCount = trackerRows.length - filledRows.length
    if (emptyRowsCount > 0) {
      toast.info(
        `${emptyRowsCount} empty ${emptyRowsCount === 1 ? 'row was' : 'rows were'} skipped`,
        { autoClose: 2000 }
      )
    }

    setIsLoading(true)

    try {
      const payload = {
        action: 'create-tracker',
        userid: userId,
        customerName: commonData.customerName.trim(),
        customerId: commonData.customerId,
        poNo: commonData.poNo.trim(),
        poDate: formatDateForApi(commonData.poDate),
        items: filledRows.map((row) => {
          const cpoBasicPrice = parseDecimal(row.cpoBasicPrice) || 0
          const spoBasicPrice = parseDecimal(row.spoBasicPrice) || 0
          const quantityOrdered = parseDecimal(row.quantityOrdered) || 0
          return {
            materialCode: row.materialCode.trim(),
            itemDesc: row.itemDesc.trim(),
            uom: row.uom.trim(),
            quantityOrdered,
            spoNo: row.spoNo.trim(),
            spoDate: formatDateForApi(row.spoDate),
            supplierName: row.supplierName.trim(),
            supplierId: row.supplierId,
            cpoBasicPrice,
            spoBasicPrice,
            total: (cpoBasicPrice - spoBasicPrice) * quantityOrdered,
            payment: row.payment,
            poStatus: row.poStatus,
            quantityDispatched: parseFloat(row.quantityDispatched) || 0,
            dispatchDate: row.dispatchDate
              ? formatDateForApi(row.dispatchDate)
              : '',
              poReceivedDate: row.poReceivedDate
              ? formatDateForApi(row.poReceivedDate)
              : '',
            deliveryDate: row.deliveryDate
              ? formatDateForApi(row.deliveryDate)
              : '',
            grnStatus: row.grnStatus,
            paymentStatus: row.paymentStatus,
            projectStatus: row.projectStatus
          }
        })
      }

      const response = await axios.post(API_URL, payload)
      const responseData = parseApiResponse(response.data)

      if (responseData.success === true) {
        toast.success(
          responseData.message ||
            `${filledRows.length} tracker ${filledRows.length > 1 ? 'entries' : 'entry'} created successfully`,
          { autoClose: 3000 }
        )
        setTimeout(() => navigate('/trackerUpdate'), 1500)
      } else {
        toast.error(responseData.message || 'Failed to create tracker entries', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Create tracker error:', error)
      if (error.response?.data) {
        const errorData = parseApiResponse(error.response.data)
        toast.error(
          errorData.message ||
            'An error occurred while creating tracker entries',
          { autoClose: 2000 }
        )
      } else {
        toast.error(
          'Network error. Please check your connection and try again.',
          { autoClose: 2000 }
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Add Customer Order</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/trackerUpdate">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card card-primary card-outline">
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fa-solid fa-table mr-2"></i>
                    Customer Order Entry Form
                  </h3>
                  <div className="card-tools">
                    <span className="badge badge-info">
                      <i className="fa-solid fa-list mr-1"></i>
                      {trackerRows.length}{' '}
                      {trackerRows.length === 1 ? 'Row' : 'Rows'}
                    </span>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="card-body">
                    <div className="row mb-4">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label style={{ fontWeight: '600', fontSize: '13px' }}>
                            PO NO
                          </label>
                          <input
                            type="text"
                            name="poNo"
                            className="form-control"
                            placeholder="PO number"
                            value={commonData.poNo}
                            onChange={handleCommonChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label style={{ fontWeight: '600', fontSize: '13px' }}>
                            PO DATE
                          </label>
                          <input
                            type="date"
                            name="poDate"
                            className="form-control"
                            value={commonData.poDate}
                            onChange={handleCommonChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label style={{ fontWeight: '600', fontSize: '13px' }}>
                            CUSTOMER NAME
                          </label>
                          <select
                            name="customerId"
                            className="form-control"
                            value={commonData.customerId}
                            onChange={handleCustomerChange}
                            disabled={isLoading || isCustomersLoading}
                            required
                          >
                            <option value="">
                              {isCustomersLoading
                                ? 'Loading customers...'
                                : customers.length === 0
                                  ? 'No customers available'
                                  : 'Select customer'}
                            </option>
                            {customers.map((customer) => (
                              <option key={customer._id} value={customer._id}>
                                {customer.customerName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    

                    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                      <table
                        className="table table-bordered table-hover mb-0"
                        style={{ width: '100%', tableLayout: 'auto' }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: '#003366', color: 'white' }}>
                            <th style={{ ...TH_STYLE, minWidth: '50px' }}>S.NO</th>
                            <th style={{ ...TH_STYLE, minWidth: '110px' }}>
                              Material Code
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '200px' }}>
                              Material Description
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '90px' }}>UOM</th>
                            <th style={{ ...TH_STYLE, minWidth: '90px' }}>
                              Qty Ordered
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '120px' }}>
                              PO Status
                            </th>
                             <th style={{ ...TH_STYLE, minWidth: '115px' }}>
                              PO Received Date
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '110px' }}>
                              SPO NO
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '120px' }}>
                              SPO DATE
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '180px' }}>
                              Supplier Name
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '110px' }}>
                              CPO Basic Price
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '110px' }}>
                              SPO Basic Price
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '100px' }}>Total Margin</th>
                            <th style={{ ...TH_STYLE, minWidth: '110px' }}>
                              Payment
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '90px' }}>
                              Qty Dispatched
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '115px' }}>
                              Dispatch Date
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '115px' }}>
                              Delivery Date
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '100px' }}>
                              GRN Status
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '110px' }}>
                              Payment Status
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '110px' }}>
                              Project Status
                            </th>
                            <th style={{ ...TH_STYLE, minWidth: '80px' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trackerRows.map((row, index) => {
                            const rowTotal = getRowTotal(row)
                            const spoRequired = row.poStatus === 'SPO Created'
                            return (
                              <tr key={row.id}>
                                <td style={{ ...TD_STYLE, textAlign: 'center' }}>
                                  {index + 1}
                                </td>
                                <td style={TD_STYLE}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Code"
                                    value={row.materialCode}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        'materialCode',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                  />
                                </td>
                                <td style={TD_STYLE}>
                                  <textarea
                                    className="form-control form-control-sm"
                                    placeholder="Description"
                                    rows={2}
                                    value={row.itemDesc}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        'itemDesc',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                    style={{ minWidth: '180px', resize: 'vertical' }}
                                  />
                                </td>
                                <td style={TD_STYLE}>
                                  <select
                                    className="form-control form-control-sm"
                                    value={row.uom || ''}
                                    onChange={(e) =>
                                      handleUpdateRow(row.id, 'uom', e.target.value)
                                    }
                                    disabled={isLoading || isLoadingUnits}
                                    style={{ minWidth: '80px' }}
                                  >
                                    <option value="">
                                      {isLoadingUnits ? 'Loading...' : 'UOM'}
                                    </option>
                                    {row.uom &&
                                      !unitOptions.some(
                                        (opt) =>
                                          opt.unit.toLowerCase() ===
                                          String(row.uom).toLowerCase()
                                      ) && (
                                        <option value={row.uom}>{row.uom}</option>
                                      )}
                                    {unitOptions.map((opt) => (
                                      <option key={opt.id || opt.unit} value={opt.unit}>
                                        {opt.unit}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td style={TD_STYLE}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="0"
                                    value={row.quantityOrdered}
                                    onChange={(e) =>
                                      handleUpdateRowNumeric(
                                        row.id,
                                        'quantityOrdered',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                  />
                                </td>
                                <td style={TD_STYLE}>
                                  <select
                                    className="form-control form-control-sm"
                                    value={row.poStatus}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        'poStatus',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                  >
                                    {PO_STATUS_OPTIONS.map((status) => (
                                      <option key={status} value={status}>
                                        {status}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                 <td style={TD_STYLE}>
                                  <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={row.poReceivedDate}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        'poReceivedDate',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                  />
                                </td>
                                <td style={TD_STYLE}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder={
                                      spoRequired ? 'SPO No *' : 'SPO No'
                                    }
                                    value={row.spoNo}
                                    onChange={(e) =>
                                      handleUpdateRow(row.id, 'spoNo', e.target.value)
                                    }
                                    disabled={isLoading}
                                    required={spoRequired}
                                  />
                                </td>
                                <td style={TD_STYLE}>
                                  <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={row.spoDate}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        'spoDate',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                    required={spoRequired}
                                  />
                                </td>
                                <td style={TD_STYLE} data-supplier-cell>
                                  <div style={{ position: 'relative', minWidth: '170px' }}>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      placeholder={
                                        spoRequired
                                          ? 'Search supplier *'
                                          : 'Search supplier'
                                      }
                                      data-supplier-input={row.id}
                                      value={row.supplierName}
                                      onChange={(e) =>
                                        handleSupplierNameChange(
                                          row.id,
                                          e.target.value
                                        )
                                      }
                                      onFocus={() => {
                                        setActiveSupplierRowId(row.id)
                                        updateSupplierDropdownPos(row.id)
                                      }}
                                      disabled={isLoading}
                                      autoComplete="off"
                                      required={spoRequired}
                                    />
                                    {activeSupplierRowId === row.id &&
                                      supplierSearchLoading && (
                                        <small className="text-muted">
                                          Searching...
                                        </small>
                                      )}
                                  </div>
                                </td>
                                <td style={TD_STYLE}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder={spoRequired ? '0 *' : '0'}
                                    value={row.cpoBasicPrice}
                                    onChange={(e) =>
                                      handleUpdateRowNumeric(
                                        row.id,
                                        'cpoBasicPrice',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                    required={spoRequired}
                                  />
                                </td>
                                <td style={TD_STYLE}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder={spoRequired ? '0 *' : '0'}
                                    value={row.spoBasicPrice}
                                    onChange={(e) =>
                                      handleUpdateRowNumeric(
                                        row.id,
                                        'spoBasicPrice',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                    required={spoRequired}
                                  />
                                </td>
                                <td style={TD_STYLE}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={formatTotalDisplay(rowTotal)}
                                    readOnly
                                    disabled
                                    placeholder="Auto"
                                    title="(CPO Basic - SPO Basic) × Qty Ordered"
                                  />
                                </td>
                                <td style={TD_STYLE}>
                                  <select
                                    className="form-control form-control-sm"
                                    value={row.payment}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        'payment',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                    required={spoRequired}
                                  >
                                    <option value="">
                                      {spoRequired ? 'Select Payment *' : 'Select Payment'}
                                    </option>
                                    {PAYMENT_OPTIONS.map((option) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td style={TD_STYLE}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="0"
                                    value={row.quantityDispatched}
                                    onChange={(e) =>
                                      handleUpdateRowNumeric(
                                        row.id,
                                        'quantityDispatched',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                  />
                                </td>
                                <td style={TD_STYLE}>
                                  <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={row.dispatchDate}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        'dispatchDate',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                  />
                                </td>
                                <td style={TD_STYLE}>
                                  <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={row.deliveryDate}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        'deliveryDate',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                  />
                                </td>
                                <td style={TD_STYLE}>
                                  <select
                                    className="form-control form-control-sm"
                                    value={row.grnStatus}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        'grnStatus',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                  >
                                    {STATUS_OPTIONS.map((status) => (
                                      <option key={status} value={status}>
                                        {status}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td style={TD_STYLE}>
                                  <select
                                    className="form-control form-control-sm"
                                    value={row.paymentStatus}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        'paymentStatus',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                  >
                                    {PAYMENT_STATUS_OPTIONS.map((status) => (
                                      <option key={status} value={status}>
                                        {status}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td style={TD_STYLE}>
                                  <select
                                    className="form-control form-control-sm"
                                    value={row.projectStatus}
                                    onChange={(e) =>
                                      handleUpdateRow(
                                        row.id,
                                        'projectStatus',
                                        e.target.value
                                      )
                                    }
                                    disabled={isLoading}
                                  >
                                    {STATUS_OPTIONS.map((status) => (
                                      <option key={status} value={status}>
                                        {status}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td style={{ ...TD_STYLE, textAlign: 'center' }}>
                                  <div className="btn-group" role="group">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-success"
                                      onClick={handleAddRow}
                                      disabled={isLoading}
                                      title="Add New Row"
                                      style={{ padding: '3px 8px' }}
                                    >
                                      <i className="fa-solid fa-plus" />
                                    </button>
                                    {trackerRows.length > 1 && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleRemoveRow(row.id)}
                                        disabled={isLoading}
                                        title="Remove This Row"
                                        style={{ padding: '3px 8px' }}
                                      >
                                        <i className="fa-solid fa-trash" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div
                    className="card-footer text-center"
                    style={{ backgroundColor: '#ffffff', padding: '20px' }}
                  >
                    <button
                      type="submit"
                      className="btn btn-lg"
                      disabled={isLoading}
                      style={{
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        padding: '10px 30px',
                        fontSize: '13px',
                        fontWeight: '600',
                        borderRadius: '25px',
                        border: 'none',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {isLoading ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-check mr-2"></i>
                          Submit
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {activeSupplierRowId != null &&
        supplierDropdownPos &&
        (() => {
          const activeRow = trackerRows.find((r) => r.id === activeSupplierRowId)
          const term = String(activeRow?.supplierName || '').trim()
          const shouldShow =
            supplierSearchResults.length > 0 ||
            (term.length >= SUPPLIER_SEARCH_MIN_LENGTH && !supplierSearchLoading)

          if (!shouldShow) return null

          return createPortal(
            <div
              data-supplier-dropdown
              style={{
                position: 'fixed',
                top: supplierDropdownPos.top,
                left: supplierDropdownPos.left,
                width: supplierDropdownPos.width,
                maxHeight: '220px',
                overflowY: 'auto',
                backgroundColor: '#fff',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                zIndex: 3000,
                boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
              }}
            >
              {supplierSearchResults.length > 0 ? (
                supplierSearchResults.map((supplier) => {
                  const id = getSupplierRecordId(supplier)
                  const name = getSupplierDisplayName(supplier)
                  return (
                    <div
                      key={id || name}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleSelectSupplier(activeSupplierRowId, supplier)
                      }}
                      style={{
                        padding: '8px 10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '12px'
                      }}
                    >
                      <strong>{name || '-'}</strong>
                      {supplier.email ? (
                        <div className="text-muted" style={{ fontSize: '11px' }}>
                          {supplier.email}
                        </div>
                      ) : null}
                    </div>
                  )
                })
              ) : (
                <div
                  style={{
                    padding: '8px 10px',
                    color: '#6c757d',
                    fontSize: '12px'
                  }}
                >
                  No suppliers found
                </div>
              )}
            </div>,
            document.body
          )
        })()}
    </div>
  )
}

export default TrackerAdd